import Image from 'next/image'
import type {
	Elements,
	ElementModels,
	IContentItem,
} from '@kontent-ai/delivery-sdk'
import type {ReactElement} from 'react'
import {clsx} from 'clsx'
import {
	Icon as CIcon,
	type IconSize,
} from '@/products/flexible-web-toolkit/components/icon'
import {loadImageFromKontentAI} from '@/image-loader'
import type {IconWithTextContentItem} from '@/products/flexible-web-toolkit/blocks/icon-with-text'
import type {Block} from '@/services/kontent-ai/types'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'
import {
	ReusableCTAButtonComponent,
	type CtaButtonContentItem,
} from '@/products/flexible-web-toolkit/blocks/cta-button'
import {spacing} from '@/products/flexible-web-toolkit/styles'
import {HtmlTextWithStyling} from './small-html-text-with-styling'
import {IconsSection} from './icons-section'

export type SmallBenefitsModuleContentItem = IContentItem<{
	iconsWithText: Elements.LinkedItemsElement<IconWithTextContentItem>
	ctaButton: Elements.LinkedItemsElement<CtaButtonContentItem>
	borderOption: Elements.MultipleChoiceElement
	squareIcon: Elements.MultipleChoiceElement
	snippetSpacingSpacing: Elements.MultipleChoiceElement
	columns: Elements.MultipleChoiceElement
}>

const SmallBenefitModuleIcon = ({
	block: icon,
	page,
	borderOption,
	squareIcon,
}: Parameters<Block<IconWithTextContentItem>>[0] & {
	borderOption: ElementModels.MultipleChoiceOption[]
	squareIcon: ElementModels.MultipleChoiceOption[]
}): ReactElement => {
	const {colorId: iconBrandColor} = colorWithFallback({
		blockColor: icon.elements.brandColor,
		blockColorLegacy: icon.elements.brand,
		pageConfig: page.elements.config[0],
		fallbackColorId: 'primary',
	})

	return (
		<div data-kontent-item-id={icon.system.id} key={icon.system.id}>
			<CIcon
				brand={iconBrandColor ?? undefined}
				className={clsx('p-1')}
				noBorder={borderOption[0]?.codename !== 'yes'}
				showSquareIcon={squareIcon[0]?.codename === 'yes'}
				size={(icon.elements.iconSize[0]?.codename || 'lg') as IconSize}
				title={
					icon.elements.title ? (
						<span
							className={
								icon.elements.boldTitle[0]?.codename === 'bold'
									? 'font-bold'
									: ''
							}
							data-kontent-element-codename="title"
						>
							{icon.elements.title}
						</span>
					) : null
				}
			>
				<div className="relative h-full w-full object-contain">
					<Image
						alt={icon.elements.icon[0]?.description ?? ''}
						data-kontent-element-codename="icon"
						height={icon.elements.icon[0]?.height || 200}
						loader={loadImageFromKontentAI}
						src={
							icon.elements.icon[0]?.url ??
							'https://assets-eu-01.kc-usercontent.com/43db687c-fb47-015f-c990-00802940e2bc/f03522f9-0615-4ca8-a83c-3eb311fd2159/placeholder-256x176.png'
						}
						width={icon.elements.icon[0]?.width || 200}
					/>
				</div>
			</CIcon>
			{icon.elements.description.value ? (
				<HtmlTextWithStyling
					className="content content--no-width-restriction leading-tight"
					data-kontent-element-codename="description"
					text={icon.elements.description.value}
				/>
			) : null}
		</div>
	)
}

export const SmallBenefitsModuleBlock: Block<
	SmallBenefitsModuleContentItem
> = ({block, page, ...context}) => {
	const componentSpacing =
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- not unnecessary, spacingChoice can be undefined
		spacing[
			block.elements.snippetSpacingSpacing[0]
				?.codename as keyof typeof spacing
		] || spacing.none

	return (
		<div className={`container-wide ${componentSpacing}`}>
			<IconsSection.Icons
				col={
					Number(block.elements.columns[0]?.name ?? 4) as
						| 1
						| 2
						| 3
						| 4
				}
			>
				{block.elements.iconsWithText.map((icon) => (
					<SmallBenefitModuleIcon
						block={icon}
						borderOption={block.elements.borderOption}
						key={icon.system.id}
						page={page}
						squareIcon={block.elements.squareIcon}
						{...context}
					/>
				))}
			</IconsSection.Icons>
			{block.elements.ctaButton.map((button) => (
				<div className="flex justify-center" key={button.system.id}>
					<ReusableCTAButtonComponent
						block={button}
						className="mt-6"
						page={page}
						{...context}
					/>
				</div>
			))}
		</div>
	)
}
